// frontend/src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from '../config/axios';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe ser usado dentro de un AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [authStateVersion, setAuthStateVersion] = useState(0);
    const verificationAttempted = useRef(false);

    // Verificar token al cargar la aplicación
    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('token');

            // Si no hay token o ya se intentó verificar, no proceder
            if (!token || verificationAttempted.current) {
                setLoading(false);
                return;
            }

            try {
                verificationAttempted.current = true;
                console.log('Verificando token de sesión...');

                const response = await axios.get('/auth/verify');
                console.log('Respuesta de verificación:', response.data);

                setUser(response.data.user);
                setError(null);
            } catch (error) {
                console.error('Error en verificación de sesión:', error);
                localStorage.removeItem('token');
                setError(error.response?.data?.message || 'Error de autenticación');
                setUser(null);
            } finally {
                setLoading(false);
            }
        };

        checkAuth();

        // Cleanup function para resetear el estado cuando el componente se desmonte
        return () => {
            verificationAttempted.current = false;
        };
    }, []);

    const resetVerificationState = () => {
        verificationAttempted.current = false;
    };

    const login = useCallback(async (email, password) => {
        try {
            const response = await axios.post('/auth/login', { email, password });
            const { token, user } = response.data;
            localStorage.setItem('token', token);
            
            // Actualizar estado de forma secuencial para asegurar reactividad
            setUser(user);
            setError(null);
            setAuthStateVersion(prev => prev + 1); // Forzar re-render
            
            // Reset verification state after successful login
            resetVerificationState();
            
            console.log('Login exitoso - Usuario actualizado:', user);
            return user;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error al iniciar sesión';
            setError(errorMessage);
            setAuthStateVersion(prev => prev + 1); // Forzar re-render incluso en error
            console.error('Error en login:', errorMessage);
            throw error;
        }
    }, []);

    const register = useCallback(async (userData) => {
        try {
            const response = await axios.post('/auth/register', userData);
            const { token, user } = response.data;
            localStorage.setItem('token', token);
            
            // Actualizar estado de forma secuencial
            setUser(user);
            setError(null);
            setAuthStateVersion(prev => prev + 1); // Forzar re-render
            
            // Reset verification state after successful registration
            resetVerificationState();
            
            console.log('Registro exitoso - Usuario creado:', user);
            return user;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error al registrarse';
            setError(errorMessage);
            setAuthStateVersion(prev => prev + 1); // Forzar re-render
            console.error('Error en registro:', errorMessage);
            throw error;
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        setUser(null);
        setError(null);
        setAuthStateVersion(prev => prev + 1); // Forzar re-render
        
        // Reset verification state after logout
        resetVerificationState();
        
        console.log('Logout exitoso - Usuario deslogueado');
    }, []);

    const addTickets = useCallback(async (amount) => {
        try {
            const response = await axios.post('/tickets/add', {
                userId: user.id,
                amount
            });
            setUser(response.data.user);
            setAuthStateVersion(prev => prev + 1); // Forzar re-render
            console.log(`Tickets agregados: ${amount}. Nuevo saldo: ${response.data.user.tickets}`);
            return response.data.user;
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error al agregar tickets';
            setError(errorMessage);
            setAuthStateVersion(prev => prev + 1); // Forzar re-render incluso en error
            console.error('Error agregando tickets:', errorMessage);
            throw error;
        }
    }, [user?.id]);

    // Nueva función para actualizar directamente los tickets del usuario sin llamada al backend
    const updateTickets = useCallback((newTicketAmount) => {
        if (user) {
            const updatedUser = { ...user, tickets: newTicketAmount };
            setUser(updatedUser);
            setAuthStateVersion(prev => prev + 1); // Forzar re-render
            console.log(`Tickets actualizados localmente. Nuevo saldo: ${newTicketAmount}`);
            return updatedUser;
        }
    }, [user]);

    // Nueva función para actualizar datos del perfil de usuario
    const updateUser = useCallback((updatedUserData) => {
        setUser(updatedUserData);
        setError(null);
        setAuthStateVersion(prev => prev + 1); // Forzar re-render
        // No es necesario actualizar el token, solo los datos del usuario
        console.log('Perfil de usuario actualizado:', updatedUserData);
    }, []);

    // Memoizar el valor del contexto para optimizar re-renders
    const value = useMemo(() => ({
        user,
        loading,
        error,
        isAuthenticated: !!user,
        authStateVersion, // Exponer versión del estado para debugging
        login,
        register,
        logout,
        addTickets,
        updateTickets, // Nueva función para actualizar tickets localmente
        updateUser // Función añadida para actualización de perfil
    }), [user, loading, error, authStateVersion, login, register, logout, addTickets, updateTickets, updateUser]);

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};